import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  getParameterByName,
  loadScriptOnce,
  prettyLog,
  taboolaLog,
} from '../../utils'
import { isNativeApp } from '../../client'
import { useRouter } from 'next/router'
import { useRouteChange } from '../../contexts'
import { IPage } from '../../types'

/** Loads taboola scripts and returns a value when it is ready */
const getTaboolaLoader = () => {
  let scriptType = 'tb_loader_script',
    loaderUrl = 'https://cdn.taboola.com/libtrc/reshet-reshet/loader.js',
    os = 'Web',
    taboolaMobilePubName = ''
  const userAgent = navigator.userAgent || navigator.vendor // || window.hasOwnProperty("opera") ? window.opera : {}

  if (isNativeApp()) {
    if (/android/i.test(userAgent)) {
      taboolaMobilePubName = 'reshet-13tvappandroid'
      scriptType = 'tb-mobile-loader-script'
      loaderUrl =
        'https://cdn.taboola.com/libtrc/reshet-13tvappandroid/mobile-loader.js'
      os = 'Android'
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      taboolaMobilePubName = 'reshet-13tvappios'
      scriptType = 'tb-mobile-loader-script'
      loaderUrl =
        'https://cdn.taboola.com/libtrc/reshet-13tvappios/mobile-loader.js'
      os = 'iOS'
    }
  }
  window.taboolaMobilePubName = taboolaMobilePubName
  return { os, scriptType, loaderUrl, taboolaMobilePubName }
}

export type TPageType =
  | null
  | { category: 'auto' }
  | { video: 'auto' }
  | { article: 'auto' }
  | { home: 'auto' }
  | { other: 'auto' }

interface ITaboolaInfo {
  isTaboolaReady: boolean
  taboolaPubName: string
  os: string
  scriptLoad: 'ready' | 'failed' | null | true | void
}

const defaultInfo: ITaboolaInfo = {
  isTaboolaReady: false,
  taboolaPubName: '',
  os: '',
  scriptLoad: null,
}
export const taboolaPush = (args: {}) => {
  if (window._taboola) {
    taboolaLog({ toBeLogged: args })
    window._taboola.push(args)
  }
}

const taboolaNewsRoomPush = (args: {} | string) => {
  if (window._newsroom) {
    taboolaLog({ toBeLogged: args, title: 'taboola_log_newsroom' })
    window._newsroom.push(args)
  }
}

export const TaboolaProvider = ({
  children,
  page,
}: {
  children: ReactNode
  page?: IPage
}) => {
  const change = useRouteChange()
  const router = useRouter()
  const [info, setInfo] = useState<ITaboolaInfo>(defaultInfo)
  const [taboolaPageType, setTaboolaPageType] = useState<TPageType>(null)
  const [chromeLess, setChromeLess] = useState<false | string>(false)
  const [showDetails, setShowDetails] = useState(false)
  const [taboolaBlocked, setTaboolaBlocked] = useState(true)
  const widgets = useRef<Map<string, boolean>>(new Map())
  const [allWidgetsLoaded, setAllWidgetsLoaded] = useState(false)

  useEffect(() => {
    setChromeLess(isNativeApp() ? '?chromeless=true' : '')
  }, [])

  useEffect(() => {
    try {
      if (!page) {
        setTaboolaBlocked(false)
      } else {
        setTaboolaBlocked(page.PageMeta.ads.block.taboola)
      }
    } catch (e) {
      prettyLog('useTaboola: 117', { e, page }, 'error')
      setTaboolaBlocked(false)
    }
  }, [page])

  useEffect(() => {
    const t =
      getParameterByName('tb_placements') == 'true' ||
      sessionStorage.getItem('tb_placements') === 'true'
    setShowDetails(t)
    sessionStorage.setItem('tb_placements', String(t))
  }, [])

  useEffect(() => {
    window._taboola = window._taboola || []
    window._newsroom = window._newsroom || []
    let unmounted = false
    ;(async () => {
      const props = getTaboolaLoader()
      const success = await loadScriptOnce({
        src: props.loaderUrl,
        id: props.scriptType,
      })
      if (unmounted) {
        return
      }
      setInfo({
        isTaboolaReady: success === 'ready',
        taboolaPubName: props.taboolaMobilePubName,
        os: props.os,
        scriptLoad: success,
      })
    })()
    return () => {
      unmounted = true
    }
  }, [info])

  useEffect(() => {
    const onRouteChangeStart = () => {
      setTaboolaPageType(null)
      widgets.current = new Map()
      setAllWidgetsLoaded(false)
    }

    router.events.on('routeChangeStart', onRouteChangeStart)
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [router.events])

  useEffect(() => {
    if (taboolaPageType && chromeLess !== false) {
      taboolaPush(taboolaPageType)
      taboolaPush({
        url: window.location.origin + window.location.pathname + chromeLess,
      })
    }
  }, [chromeLess, taboolaPageType])

  useEffect(() => {
    allWidgetsLoaded && taboolaPush({ flush: true })
  }, [allWidgetsLoaded])

  useEffect(() => {
    taboolaLog({ toBeLogged: { taboolaBlocked } })
  }, [taboolaBlocked])

  useEffect(() => {
    if (change.length > 0 && window.location.pathname === '/') {
      taboolaNewsRoomPush({ pageTemplate: 'home' })
      taboolaNewsRoomPush({ pageDashboard: 'home' })
      taboolaNewsRoomPush('auditClicks')
      taboolaNewsRoomPush('trackPage')
      taboolaNewsRoomPush({ getAction: 'true' })
    }
  }, [change.length])

  useEffect(() => {
    if (change.length < 2 || !window._taboola) {
      return
    }
    taboolaPush({ notify: 'newPageLoad' })
  }, [change])

  const testAllWidgetLoaded = useCallback(() => {
    // taboolaLog({ toBeLogged: { widgets: widgets.current, allWidgetsLoaded } })
    let loaded = 0
    widgets.current.size > 0 &&
      widgets.current.forEach((v, k) => {
        if (!document.getElementById(k)) {
          loaded++
        } else {
          v && loaded++
        }
      })
    if (
      widgets.current.size > 0 &&
      allWidgetsLoaded != (loaded === widgets.current.size)
    ) {
      setAllWidgetsLoaded(loaded === widgets.current.size)
    }
  }, [allWidgetsLoaded])

  const addWidget = useCallback((name: string) => {
    if (!widgets.current.has(name)) {
      widgets.current.set(name, false)
    }
  }, [])

  const removeWidget = useCallback(
    (key: string) => {
      if (widgets.current.has(key)) {
        widgets.current.delete(key)
      }
      testAllWidgetLoaded()
    },
    [testAllWidgetLoaded]
  )

  const updateWidget = (id: string, newValue: boolean) => {
    const oldValue = widgets.current.get(id)
    if (oldValue !== newValue) {
      widgets.current.set(id, newValue)
    }
    // taboolaLog({ toBeLogged: widgets.current })
    testAllWidgetLoaded()
  }

  return (
    <TaboolaContext.Provider
      value={{
        taboolaPageType,
        setTaboolaPageType: setTaboolaPageType,
        taboolaInfo: info,
        showDetails,
        taboolaBlocked,
        addWidget,
        removeWidget,
        updateWidget,
      }}
    >
      {children}
    </TaboolaContext.Provider>
  )
}

interface ITaboolaContext {
  taboolaPageType: TPageType
  setTaboolaPageType: React.Dispatch<React.SetStateAction<TPageType>>
  taboolaInfo: ITaboolaInfo
  showDetails: boolean
  taboolaBlocked: boolean
  addWidget: (name: string) => void
  removeWidget: (name: string) => void
  updateWidget: (name: string, loaded: boolean) => void
}

const defaultState = {
  taboolaPageType: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTaboolaPageType: () => {},
  taboolaInfo: defaultInfo,
  showDetails: false,
  taboolaBlocked: true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addWidget: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeWidget: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateWidget: () => {},
} as ITaboolaContext
const TaboolaContext = createContext<ITaboolaContext>(defaultState)
export const useTaboola = () => useContext(TaboolaContext)
